import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import usePriceSettings from "../../data/usePriceSettings";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import {
  ICustomerGenericPriceSetting,
  ICustomerPriceConfiguration,
} from "../../models/PriceSettings";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import GeneralPriceSettingSingle from "./PriceConfiguration/GeneralPriceSettingSingle";
import LoadingWheel from "../shared/LoadingWheel";
import SpecificPriceSingle from "./SpecificConfiguration/SpecificPriceSingle";
import SpecificPriceTable from "./SpecificConfiguration/SpecificPriceTable";
import SpecificPriceModal from "./SpecificConfiguration/SpecificPriceModal";
import { PlusSmallIcon } from "@heroicons/react/20/solid";
import ImportFromCsv from "../shared/ImportFromCsv";
import usePriceSettingsMutations from "../../data/usePriceSettingsMutations";
import FullSpecificPriceTable from "./SpecificConfiguration/FullSpecificPriceTable";
import SpecificRuleTestModal from "./SpecificRuleTestModal";
import SpecificPriceCsvModal, { useSpecificPriceCsvExport } from "./SpecificConfiguration/SpecificPriceCsvModal";
import { ArrowDownTrayIcon, ArrowUpTrayIcon, BeakerIcon } from "@heroicons/react/24/outline";

interface IGenericCSVPrototype {
  make: string;
  model: string;
  fuelType: string;
  engineCode: string;
  fromYear: number;
  toYear: number;
  fromEngineCc: number;
  toEngineCc: number;
  value: number;
  isFixed: boolean;
}


export default function SpecificPriceSettings() {
  const { priceSettings } = usePriceSettings();
  const { exportCsv, exporting } = useSpecificPriceCsvExport();
  const { createBulkSpecific } = usePriceSettingsMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Price Adjustments");
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkAdd, setBulkAdd] = useState(false);
  const [editSetting, setEditSetting] = useState<ICustomerGenericPriceSetting>();
  const [csvOpen, setCsvOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [specificPriceSettings, setSpecificPriceSettings] = useState<
    ICustomerGenericPriceSetting[]
  >([]);

  const prototype: any = {
    make: "",
    model: "",
    fuelType: "",
    engineCode: "",
    fromYear: 0,
    toYear: 0,
    fromEngineCc: 0,
    toEngineCc: 0,

    
    value: 0,
    isFixed: true,
  };

  const handleBulkImport = (postcodeGroups: IGenericCSVPrototype[]) => {
    const items: any[] = postcodeGroups.map((item) => ({
      make         : item.make,
      model        : item.model,
      fuelType     : item.fuelType,
      engineCode   : item.engineCode,
      fromYear     : item.fromYear     == 0 ? null : item.fromYear,
      toYear       : item.toYear       == 0 ? null : item.toYear,
      fromEngineCc : item.fromEngineCc == 0 ? null : item.fromEngineCc,
      toEngineCc   : item.toEngineCc   == 0 ? null : item.toEngineCc,
      priceSetting : {
        value   : item.value,
        isFixed : item.isFixed,
      },
    }));

    createBulkSpecific.mutate(items, {
      onSuccess: () => {
        setCsvOpen(false);
        setEditSetting(undefined);
        setModalOpen(false);
        saveHandlers.onSuccess();
      },
      onError: (error) => {
        console.error("Bulk import failed:", error);
        saveHandlers.onError();
      },
    });
  }

  useEffect(() => {
    if (!priceSettings.isLoading) {
      if (priceSettings.data?.data.genericPriceSettings && priceSettings.data?.data.genericPriceSettings.length > 0) {
        setSpecificPriceSettings(priceSettings.data?.data.genericPriceSettings);
      }
      setIsLoading(false);
    }
  }, [priceSettings.data, priceSettings.isLoading]);

  const handleSetSpecificPriceSetting = (specificPriceSetting: ICustomerGenericPriceSetting) => {
    setEditSetting(specificPriceSetting);
    setBulkAdd(false);
    setModalOpen(true);
  }

  const handleAddSpecificPriceSetting = () => {
    setEditSetting(undefined);
    setBulkAdd(false);
    setModalOpen(true);
  }

  const handleBulkAddSpecificPriceSetting = () => {
    setEditSetting(undefined);
    setBulkAdd(true);
    setModalOpen(true);
  }

  if (isLoading) return <LoadingWheel />;

  return (
    <>
      <SpecificPriceModal 
        open={modalOpen} 
        setOpen={setModalOpen} 
        customerPriceConfigurationId={priceSettings.data?.data.customerPriceConfiguration?.id!} 
        specificPriceSetting={editSetting}
        bulkAdd={bulkAdd}
      />

      <SpecificPriceCsvModal open={importOpen} setOpen={setImportOpen} />

      <SpecificRuleTestModal open={testModalOpen} setOpen={setTestModalOpen} />

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => exportCsv()}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {exporting ? "Exporting..." : "Export to CSV"}
        </button>

        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          Import from CSV
        </button>

        <button
          type="button"
          onClick={() => setTestModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors"
        >
          <BeakerIcon className="h-4 w-4" />
          Check Rules Against Reg
        </button>
      </div>

      {/* <div className="bg-white shadow rounded p-4">

        <h1 className="text-xl font-semibold">Create New Setting</h1>
        <hr />

        <SpecificPriceSingle customerPriceConfigurationId={priceSettings.data?.data.customerPriceConfiguration?.id!} />
      </div> */}

      <div className="mt-4 flex justify-between">

        {/* <button
          className=" bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
          type="button"
          onClick={() => handleAddSpecificPriceSetting()}
        >
          <PlusSmallIcon className="h-5 w-5 mr-2" />
          Add New Setting
        </button> */}

        {/* <button
          className=" bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
          type="button"
          onClick={() => handleBulkAddSpecificPriceSetting()}
        >
          <PlusSmallIcon className="h-5 w-5 mr-2" />
          Vehicle Lookup
        </button> */}

        {/* <ImportFromCsv<IGenericCSVPrototype> prototypeObject={prototype} onSuccess={handleBulkImport} open={csvOpen} setOpen={setCsvOpen} /> */}
      </div>

      <div className="bg-white shadow rounded p-4 mt-4">
{/* 
        <h1 className="text-xl font-semibold">All Settings</h1>
        <hr /> */}

        <div className="flex flex-col gap-y-6">

          {/* {specificPriceSettings.map((priceSetting) => (
            <SpecificPriceSingle key={priceSetting.id} customerPriceConfigurationId={priceSettings.data?.data.customerPriceConfiguration?.id!} specificPriceSetting={priceSetting} />
          ))} */}

          {/* <SpecificPriceTable 
            specificPriceSettings={specificPriceSettings} 
            setSpecificPriceSetting={handleSetSpecificPriceSetting} 
          /> */}


          <FullSpecificPriceTable />

          {/* <div className="grid grid-cols-2 gap-4">
            <ButtonInput 
              label="Import Specific Settings from CSV" 
              onClick={() => setImportOpen(true)}
              isSubmit={false}
            />

            <ButtonInput 
              label="Export All Specific Settings to CSV" 
              onClick={() => handleCsvExport()}
              isSubmit={false}
            />
          </div> */}

        </div>
      </div>
    </>

  )
}
